import { WeatherRequestInput, WeatherWidget } from '@/app/models/weather-widget.model';
import { WeatherService } from '@/app/services/weather.service';
import { WidgetHeaderComponent } from '@/app/shared/components/widget-header/widget-header.component';
import { ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError, filter, finalize, switchMap, tap } from 'rxjs/operators';
import { AccuWeatherLogoComponent } from './components/accu-weather-logo/accu-weather-logo.component';
import { WeatherBottomComponent } from './components/weather-bottom/weather-bottom.component';
import { WeatherMiddleComponent } from './components/weather-middle/weather-middle.component';
import { WeatherTopComponent } from './components/weather-top/weather-top.component';
import { WeatherSettingsService } from './services/weather-settings.service';
import { WeatherStore } from './services/weather.store';
import { WeeklyWeather } from './models/weather.model';
import { HomeService } from '@/app/services/home.service';

@Component({
  selector: 'dr-flex-home-ui-weather-widget',
  standalone: true,
  imports: [CommonModule, TranslateModule, WidgetHeaderComponent, WeatherTopComponent, WeatherMiddleComponent, WeatherBottomComponent, AccuWeatherLogoComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './weather-widget.component.html',
  styleUrls: ['./weather-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WeatherWidgetComponent implements OnInit {
  readonly widget = input.required<WeatherWidget>();
  private readonly weatherService = inject(WeatherService);
  private readonly saveWeatherSettings = inject(WeatherSettingsService);
  private readonly getWeatherUserSettings = inject (HomeService)
  readonly store = inject(WeatherStore);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    // fetch weather data when signals change
    this.setupWeatherSync();
    // save to backend on manual triggers
    this.setupManualSaveSync();
  }

  ngOnInit(): void {
    this.initializeWidgetConfig();
  }

  /**
   * Listens for the userSettingsChanged$ from the store.
   * This only fires when setAndSaveLocation or setAndSaveUnit are called.
   */
  private setupManualSaveSync(): void {
    this.store.userSettingsChanged$
      .pipe(
        switchMap(() => {
          const location = this.store.location();
          const unit = this.store.unit();

          if (!location) return of(null);

          return this.saveWeatherSettings.saveUserWeatherSettings({
            location_id: location.key,
            display_label: location.name,
            unit: unit,
            provider: 'ACCUWEATHER',
          });
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  /**
   * Watches the location and unit signals.
   * Automatically fetches weather data whenever they change
   */
  private setupWeatherSync(): void {
    toObservable(
      computed(() => ({
        location_id: this.store.selectedLocationId(),
        unit: this.store.unit(),
      }))
    )
      .pipe(
        filter((params) => !!params.location_id),
        switchMap((params) => this.loadWeatherForecast(params)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  /**
   * Loads initial settings. Uses patchConfig to update signals
   */
  private initializeWidgetConfig(): void {
    this.getWeatherUserSettings.getWeatherSettings()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((settings) => {
          if (!settings) {
            this.store.setError('Failed to load weather settings');
            return;
          }
          this.store.patchConfig(settings.unit, {
            key: settings.location_id,
            name: settings.display_label,
          });
        })
      )
      .subscribe();
  }

  /**
   * Fetches weather forecast based on the provided input.
   * Manages loading and error states in the store.
   * Returns an Observable of the weather data or null if failed.
   */
  private loadWeatherForecast(input: WeatherRequestInput): Observable<WeeklyWeather | null> {
    return of(null).pipe(
      tap(() => {
        this.store.setLoading(true);
        this.store.setError(null);
      }),
      switchMap(() => this.weatherService.getForecast(input)),
      tap((payload) => {
        if(payload){
          this.store.setData(payload);
        }else{
          this.store.setError('No data received');
        }
        return;
      }),
      catchError(() => {
        this.store.setError('Service unavailable');
        return of(null);
      }),
      finalize(() => this.store.setLoading(false))
    );
  }
}
